import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE, setAccessToken } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input, Label } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { RaptorLogo } from '@/components/RaptorLogo';
import { toast } from 'sonner';
import { ShieldCheck } from 'lucide-react';

interface Resolved { broker_id: string; brokerage_name: string; insurer_id: string }

export const PublicRegistration: React.FC = () => {
  const { token } = useParams();
  const nav = useNavigate();
  const { refreshMe } = useAuth();
  const [step, setStep] = useState<'phone' | 'otp' | 'details' | 'done' | 'error'>('phone');
  const [broker, setBroker] = useState<Resolved | null>(null);
  // Registration identifier. Either 'phone' (SMS OTP) or 'email' (email
  // OTP). Broker staff without a corporate mobile number can register
  // via email as long as the brokerage has whitelisted their email
  // address on the broker_staff record.
  const [identifierMode, setIdentifierMode] = useState<'phone' | 'email'>('phone');
  const [phone, setPhone] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [otp, setOtp] = useState('');
  const [details, setDetails] = useState({ full_name: '', email: '', password: '', confirm: '' });
  const [errMsg, setErrMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get<Resolved>(`${API_BASE}/public/registration/resolve/${token}`)
      .then(r => setBroker(r.data))
      .catch((e) => { setStep('error'); setErrMsg(e?.response?.data?.detail ?? 'Invalid link'); });
  }, [token]);

  const startOtp = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault(); setBusy(true);
    try {
      const body: { phone?: string; email?: string } = identifierMode === 'email'
        ? { email: regEmail }
        : { phone };
      const r = await axios.post<{ challenge_id: string; expires_min: number; channel?: string; identifier_masked?: string }>(
        `${API_BASE}/public/registration/${token}/start`, body,
      );
      setChallengeId(r.data.challenge_id); setStep('otp');
      const via = r.data.channel === 'email' ? 'email' : 'SMS';
      toast.success(`OTP sent via ${via} · valid ${r.data.expires_min} min`);
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Blocked');
    } finally { setBusy(false); }
  };

  const verify = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault(); setBusy(true);
    try {
      await axios.post(`${API_BASE}/public/registration/${token}/verify`, { challenge_id: challengeId, code: otp });
      toast.success('OTP verified'); setStep('details');
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Invalid code');
    } finally { setBusy(false); }
  };

  const complete = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (details.password !== details.confirm) { toast.error('Passwords do not match.'); return; }
    setBusy(true);
    try {
      const r = await axios.post<{ access_token: string }>(`${API_BASE}/public/registration/${token}/complete`, {
        challenge_id: challengeId, full_name: details.full_name, email: details.email, password: details.password,
      }, { withCredentials: true });
      setAccessToken(r.data.access_token);
      await refreshMe();
      toast.success('Welcome to Raptor');
      setStep('done');
      setTimeout(() => nav('/broker', { replace: true }), 1200);
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Registration failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="flex-1 grid md:grid-cols-2 min-h-[calc(100vh-32px)]">
      <div className="raptor-hero relative overflow-hidden hidden md:flex flex-col justify-between p-10 bg-raptor-hero">
        <RaptorLogo size={44} monochrome="white" />
        <div className="relative z-10">
          <h2 className="font-headline text-white text-4xl tracking-wide leading-tight">Broker onboarding</h2>
          <p className="text-white/80 font-body mt-4 max-w-md">Your approved phone number or email is the security gate. We&apos;ll send you a one-time code, then you set your password. Nothing personal is required beyond your name.</p>
          <div className="mt-4 flex items-center gap-2 text-white/90 text-sm font-body">
            <ShieldCheck size={16} className="text-raptor-cyan" /> Every step is logged for FSCA + Guardrisk audit.
          </div>
        </div>
        <div className="text-white/60 text-xs font-body">Powered by Raptor · Osprey UMA</div>
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-raptor-cyan/20 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-lg">{broker?.brokerage_name ?? 'Loading broker…'}</CardTitle>
            <CardDescription>Registering under insurer <span className="font-tabular">{broker?.insurer_id ?? '—'}</span></CardDescription>
          </CardHeader>
          <CardContent>
            {step === 'error' && <div className="text-red-600 font-body">{errMsg}</div>}

            {step === 'phone' && (
              <form onSubmit={startOtp} className="space-y-4">
                <div className="flex gap-2 border border-border/60 rounded-md p-1 bg-raptor-neutral/30" role="tablist" aria-label="Registration channel">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={identifierMode === 'phone'}
                    className={`flex-1 rounded px-2 py-1 text-sm font-body transition-colors ${identifierMode === 'phone' ? 'bg-white text-raptor-navy shadow-sm' : 'text-raptor-navy/60 hover:text-raptor-navy'}`}
                    onClick={() => setIdentifierMode('phone')}
                    data-testid="reg-mode-phone"
                  >Phone (SMS)</button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={identifierMode === 'email'}
                    className={`flex-1 rounded px-2 py-1 text-sm font-body transition-colors ${identifierMode === 'email' ? 'bg-white text-raptor-navy shadow-sm' : 'text-raptor-navy/60 hover:text-raptor-navy'}`}
                    onClick={() => setIdentifierMode('email')}
                    data-testid="reg-mode-email"
                  >Email</button>
                </div>

                {identifierMode === 'phone' ? (
                  <div className="space-y-1.5">
                    <Label>Your approved phone number</Label>
                    <Input data-testid="reg-phone" required placeholder="0821234567 or +27821234567" value={phone} onChange={(e) => setPhone(e.target.value)} />
                    <p className="text-xs text-raptor-navy/60 font-body">Only numbers on your brokerage&apos;s approved list can proceed.</p>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label>Your approved email address</Label>
                    <Input data-testid="reg-email" required type="email" placeholder="you@brokerage.co.za" value={regEmail} onChange={(e) => setRegEmail(e.target.value)} />
                    <p className="text-xs text-raptor-navy/60 font-body">Only emails on your brokerage&apos;s approved list can proceed. Use this if your admin invited you by email instead of phone.</p>
                  </div>
                )}
                <Button data-testid="reg-send-otp" type="submit" variant="gradient" className="w-full" disabled={busy}>{busy ? 'Sending…' : 'Send OTP'}</Button>
              </form>
            )}

            {step === 'otp' && (
              <form onSubmit={verify} className="space-y-4">
                <div className="space-y-1.5">
                  <Label>Enter the 6-digit code</Label>
                  <Input data-testid="reg-otp" required inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value)} className="font-tabular text-lg tracking-widest text-center" />
                  <p className="text-xs text-raptor-navy/60 font-body">In dev, the code appears in System Health → Recent stub SMS.</p>
                </div>
                <Button data-testid="reg-verify-otp" type="submit" variant="gradient" className="w-full" disabled={busy}>{busy ? 'Verifying…' : 'Verify'}</Button>
              </form>
            )}

            {step === 'details' && (
              <form onSubmit={complete} className="space-y-4">
                <div className="space-y-1.5"><Label>Full name</Label>
                  <Input data-testid="reg-fullname" required value={details.full_name} onChange={(e) => setDetails({ ...details, full_name: e.target.value })} />
                </div>
                <div className="space-y-1.5"><Label>Email</Label>
                  <Input data-testid="reg-email" type="email" required value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} />
                </div>
                <div className="space-y-1.5"><Label>Password (min 12, upper/lower/digit/symbol)</Label>
                  <Input data-testid="reg-password" type="password" required value={details.password} onChange={(e) => setDetails({ ...details, password: e.target.value })} />
                </div>
                <div className="space-y-1.5"><Label>Confirm password</Label>
                  <Input data-testid="reg-password-confirm" type="password" required value={details.confirm} onChange={(e) => setDetails({ ...details, confirm: e.target.value })} />
                </div>
                <Button data-testid="reg-complete" type="submit" variant="gradient" className="w-full" disabled={busy}>{busy ? 'Creating…' : 'Create account'}</Button>
              </form>
            )}

            {step === 'done' && (
              <div className="text-center py-6">
                <div className="font-headline text-2xl text-raptor-navy">Access granted</div>
                <div className="text-sm text-raptor-navy/60 font-body mt-2">Redirecting to your broker portal…</div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
